// @flow

import Input from 'shared/DEPRECATED/components/Input';
import TextArea from 'shared/components/TextArea';
import WebOrMobileDropdown from 'shared/components/web_or_mobile_dropdown';
import S3FileUploader from 'shared/components/files/S3FileUploader';
import uuid from 'uuid';
import React from 'react';
import styled from 'styled-components';
import { FormLabel } from 'shared/DEPRECATED/components/form/layout';
import { colors, fontWeight } from '@grnhse/seedling/lib/azalea/constants';
import t from 'shared/utils/translation';

const MAX_UPLOAD_SIZE = 50000000; // 50MB

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
const CandidateFacingFormLabel = styled(FormLabel)`
  color: ${colors.darkestGrey};
  line-height: 22px;

  // to preserve line breaks but collapse white-space
  white-space: pre-line;
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
const LegendLabel = CandidateFacingFormLabel.withComponent('legend');

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
const SectionHeader = styled.h2`
  font-size: 16px;
  ${fontWeight('heavy')};
  width: 100%;
  padding-top: 20px;
  padding-bottom: 15px;
  border-bottom: 1.5px solid ${colors.darkestGrey};
`;

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
const Statement = styled.div`
  line-height: 22px;

  ol,
  ul {
    margin: 1em 0;
    padding: 0 0 0 40px;
  }

  ol li {
    list-style-type: decimal;
  }

  ul li {
    list-style-type: disc;
  }

  strong,
  b {
    ${fontWeight('heavy')};
  }
`;

type AnswerValue = any;

type Answer = {
  value_type:
    | 'short_text'
    | 'long_text'
    | 'yes_no'
    | 'single_select'
    | 'multi_select'
    | 'attachment'
    | 'statement'
    | 'header',
  htmlId: string,
  question_name: string,
  required: string,
  value: AnswerValue,
};

type AnswerReplacer = (AnswerValue, AnswerValue) => AnswerValue;

type UpdateAnswerCallback = (Answer, AnswerValue, ?AnswerReplacer) => void;

export default class AnswerInputFactory {
  updateAnswerValue: UpdateAnswerCallback; // callback for when this answer's value changes
  setUploadError: (any) => void; // callback if there was a file upload error
  signedUrl: string; // pre-signed S3 URL
  allowlistedExtensions: Array<string>; // list of file extensions for attachment-type fields
  disableSubmit: boolean; // true if the attachment control should be disabled
  touchFriendly: boolean; // true to render mobile friendly controls

  constructor({
    updateAnswerValue,
    setUploadError,
    signedUrl,
    allowlistedExtensions,
    disableSubmit,
    touchFriendly,
  }: {
    updateAnswerValue: UpdateAnswerCallback,
    setUploadError: (any) => void,
    signedUrl: string,
    allowlistedExtensions: Array<string>,
    disableSubmit: boolean,
    touchFriendly: boolean,
  }) {
    this.updateAnswerValue = updateAnswerValue;
    this.setUploadError = setUploadError;
    this.signedUrl = signedUrl;
    this.allowlistedExtensions = allowlistedExtensions;
    this.disableSubmit = disableSubmit;
    this.touchFriendly = touchFriendly;
  }

  /**
   * Returns a React form field component for the provided answer.  The type of component is different based on the
   * answer's CustomField type.
   */
  componentFor(answer: Answer) {
    switch (answer.value_type) {
      case 'short_text':
        return AnswerInputFactory.shortTextFor(answer, this.updateAnswerValue);
      case 'long_text':
        return AnswerInputFactory.longTextFor(answer, this.updateAnswerValue);
      case 'yes_no':
        return AnswerInputFactory.yesNoFor(answer, this.updateAnswerValue);
      case 'single_select':
        return AnswerInputFactory.singleSelectFor(
          answer,
          this.updateAnswerValue,
          this.touchFriendly
        );
      case 'multi_select':
        return AnswerInputFactory.multiSelectFor(
          answer,
          this.updateAnswerValue,
          this.touchFriendly
        );
      case 'attachment':
        return AnswerInputFactory.attachmentFor(
          answer,
          this.updateAnswerValue,
          this.setUploadError,
          this.signedUrl,
          this.allowlistedExtensions,
          this.disableSubmit
        );
      case 'header':
        return AnswerInputFactory.headerFor(answer);
      case 'statement':
        return AnswerInputFactory.statementFor(answer);
      default:
        throw `Unknown question type: ${answer.value_type}`;
    }
  }

  static labelFor(answer: Answer) {
    return (
      <CandidateFacingFormLabel
        htmlFor={answer.htmlId}
        required={answer.required === 'true'}
      >
        {answer.question_name}
      </CandidateFacingFormLabel>
    );
  }

  static shortTextFor(answer: Answer, updateAnswerValue: UpdateAnswerCallback) {
    return (
      <div>
        {AnswerInputFactory.labelFor(answer)}
        <Input
          id={answer.htmlId}
          type="text"
          value={answer.value}
          data-test={answer.htmlId}
          onChange={(e) => updateAnswerValue(answer, e.target.value)}
          block
        />
      </div>
    );
  }

  static longTextFor(answer: Answer, updateAnswerValue: UpdateAnswerCallback) {
    return (
      <div>
        {AnswerInputFactory.labelFor(answer)}
        <TextArea
          id={answer.htmlId}
          rows="5"
          value={answer.value}
          onChange={(e) => updateAnswerValue(answer, e.target.value)}
          data-test={answer.htmlId}
          block
        />
      </div>
    );
  }

  static yesNoFor(answer: Answer, updateAnswerValue: UpdateAnswerCallback) {
    return (
      <fieldset id={answer.htmlId}>
        <LegendLabel required={answer.required === 'true'}>
          {answer.question_name}
        </LegendLabel>

        <div style={{ marginBottom: '10px', marginTop: '10px' }}>
          <label id={`yes_label_for_${answer.htmlId}`}>
            <input
              style={{ marginRight: '10px' }}
              type="radio"
              value="Yes"
              checked={answer.value === true}
              onChange={() => updateAnswerValue(answer, true)}
            />
            {t('forms.answers.affirmative')}
          </label>
        </div>

        <div>
          <label>
            <input
              style={{ marginRight: '10px' }}
              type="radio"
              value="No"
              checked={answer.value === false}
              onChange={() => updateAnswerValue(answer, false)}
            />
            {t('forms.answers.negative')}
          </label>
        </div>
      </fieldset>
    );
  }

  static singleSelectFor(
    answer: Answer,
    updateAnswerValue: UpdateAnswerCallback,
    touchFriendly: boolean
  ) {
    return (
      <div>
        {answer.question_name}
        <WebOrMobileDropdown
          inputId={answer.htmlId}
          multiSelect={false}
          touchFriendly={touchFriendly}
          question={answer}
          onChange={(value) =>
            updateAnswerValue(answer, value[0] ? value[0].id : null)
          }
          value={answer.value}
        />
      </div>
    );
  }

  static multiSelectFor(
    answer: Answer,
    updateAnswerValue: UpdateAnswerCallback,
    touchFriendly: boolean
  ) {
    return (
      <div>
        {answer.question_name}
        <WebOrMobileDropdown
          inputId={answer.htmlId}
          multiSelect={true}
          touchFriendly={touchFriendly}
          question={answer}
          onChange={(value) =>
            updateAnswerValue(
              answer,
              value.map((option) => option.id)
            )
          }
          value={answer.value}
        />
      </div>
    );
  }

  static attachmentFor(
    answer: Answer,
    updateAnswerValue: UpdateAnswerCallback,
    setUploadError: (any) => void,
    signedUrl: string,
    allowlistedExtensions: Array<string>,
    disableSubmit: boolean
  ) {
    return (
      <div>
        {this.labelFor(answer)}

        <S3FileUploader
          id={uuid.v4()}
          signingUrl={signedUrl}
          acceptedExtensions={allowlistedExtensions}
          onFileFinished={(upload) => {
            setUploadError(null);
            updateAnswerValue(answer, upload.key, (existingValue, value) =>
              existingValue ? [existingValue, value].join() : value
            );
          }}
          maxSize={MAX_UPLOAD_SIZE}
          onFileRemoved={(upload) => {
            updateAnswerValue(answer, upload.key, (existingValue, value) =>
              existingValue
                .split(',')
                .filter((val) => val !== value)
                .join()
            );
          }}
          multiple
          disabled={disableSubmit}
        />
      </div>
    );
  }

  static headerFor(answer: Answer) {
    return <SectionHeader>{answer.question_name}</SectionHeader>;
  }

  static statementFor(answer: Answer) {
    return (
      <Statement dangerouslySetInnerHTML={{ __html: answer.question_name }} />
    );
  }
}
